import React, { useEffect, useMemo } from 'react';
import { Spin, Modal } from 'antd';
import { useDispatch } from 'react-redux';
import { FormProps } from 'antd/lib/form';
import { useDebouncedCallback } from 'use-debounce';
import produce from 'immer';
import { assign, find, get, isArray, isEmpty, isEqual, mergeWith } from 'lodash';
import { useLocation } from 'react-router';
import { useWatch } from 'antd/lib/form/Form';
import { useTranslation } from 'react-i18next';

import { ApplicationPayload } from '~/types/ApplicationPayload';
import { useGetApplication, useSubmitStep } from '~/controllers/wizard';
import { useMutableCallback } from '~/hooks';
import { useCommonErrors, useValidator } from '~/controllers/validation';
import { useDraftIsStale, useGetDraft, useSaveDraft } from '~/controllers/draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { getRoles } from '~/utils/getDecodedJwt';
import { isReadOnly } from '~/utils/isReadOnly';
import { isSomeFieldsDisabled } from '~/utils/isSomeFieldsDisabled';
import { getStagePayload } from '~/utils/getStagePayload';
import { useGetCardHolderError } from '~/controllers/cardholder';
import { setAddingExistingUserValues } from '~/slices/state';
import { getContactDataFromUpdatedList } from '~/utils/contacts';
import { getChangedPaths } from '~/utils/getChangedPaths';
import { useMutateContactsApi } from '~/controllers/contacts';

import { FormItemContextProvider } from '../Form/FormItemContext';
import Form from '../Form';

const SAVE_DRAFT_DEBOUNCE_MS = 1000;

interface WizardFormProps<Values> extends Omit<FormProps<Values>, 'initialValue'> {
  stepKey: keyof ApplicationPayload;
  validationFnName?: string;
  disabled?: boolean;
  onBeforeFinish?: (values: Values) => Promise<any>;
  parseBeforeSubmit?: any;
  handleDraftDataBeforeSave?: (values: Values, initialValues?: Values) => Values;
  handleContactList?: (value: Values) => Values;
  setInitialValues?: (initialValue: Values) => Values;
  getOptionalFields?: (values: Values) => Record<string, string>;
  validationValues?: Values;
  withAutoSaveDraft?: boolean;
  shouldSaveDraft?: (changedValues?: any, values?: any) => boolean;
  withDefaultOnFinish?: boolean;
  withDefaultValidation?: boolean;
  onBeforeSubmit?: any;
}

function WizardForm<Values extends Record<string, any> = ApplicationPayload>(
  props: WizardFormProps<Values>,
): JSX.Element {
  const {
    stepKey,
    onBeforeSubmit,
    validationFnName,
    onValuesChange,
    onBeforeFinish,
    onFinishFailed,
    parseBeforeSubmit,
    handleDraftDataBeforeSave = (values, initialValues) => {
      return produce<any>(initialValues, (draft) => {
        // TODO: find out purpose of this code. I totally don't remember :(
        mergeWith(draft, values, (draftValue, value) => {
          if (Array.isArray(value)) {
            return value;
          }
          return undefined;
        });
      });
    },
    handleContactList = (values) => {
      return {
        data: find(values.authorizedRepresentative, (individual) => individual),
      };
    },
    getOptionalFields,
    form: formFromProps,
    disabled,
    setInitialValues,
    shouldSaveDraft = () => true,
    withAutoSaveDraft = true,
    withDefaultOnFinish = true,
    withDefaultValidation = true,
    name,
    ...rest
  } = props;
  const dispatch = useDispatch();
  // location.pathname is also default 'formName' prop for submit button
  const location = useLocation();
  const actualFormName = name ?? location.pathname;
  const draftQuery = useGetDraft<Values>();
  const draftMutation = useSaveDraft();
  const [, handleCommonErrors] = useCommonErrors(actualFormName);
  const applicationQuery = useGetApplication();

  const cardholderErrors = useGetCardHolderError(actualFormName);

  // Validation
  const [form] = Form.useForm<Values>(formFromProps);

  const { t } = useTranslation('fillApplication');
  const uiKey = getStagePayload.getUiKey(applicationQuery?.data);
  const isNewClient = get(applicationQuery?.data, 'payload.isNewClient');
  const applicationPayloadStepData = uiKey && get(applicationQuery?.data, ['payload', uiKey]);
  const draftStepData: any = uiKey && get(draftQuery?.data, [uiKey]);

  const getFormState = () => form.getFieldsValue();
  const validatorController = useValidator(getFormState, validationFnName);

  // initialValues
  const isFormNotReady = (draftQuery.isLoading && !draftQuery.data) || applicationQuery.isLoading;
  const initialValues = useMemo(() => {
    if (!isFormNotReady && applicationQuery.data) {
      const rawPayload =
        draftQuery.data ||
        ({
          [stepKey]: applicationQuery.data?.payload?.[stepKey] ?? {},
        } as Values);
      const result = setInitialValues ? setInitialValues(rawPayload) : rawPayload;
      return result;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormNotReady]);

  //
  const optionalFieldsController = useOptionalFields({
    getOptionalFields,
    validationFnName,
    initialValues,
  });

  // Draft
  const { onFresh, onStale } = useDraftIsStale();

  // contact Details
  const contactsMutation = useMutateContactsApi();

  const debouncedSaveChanges = useDebouncedCallback(async (formValues: any) => {
    const stepData = handleDraftDataBeforeSave(formValues, initialValues);
    const updatedAuthorizedRepresentative = handleContactList(formValues);
    if (!isEqual(stepData[stepKey], initialValues?.[stepKey])) {
      await draftMutation.mutateAsync({ stepData });
    }

    if (isEqual(actualFormName, '/wizard/application/authorizedRepresentative')) {
      const updatedContactList = await getContactDataFromUpdatedList(updatedAuthorizedRepresentative);
      const addIndivisualToContactList = assign(updatedContactList, updatedAuthorizedRepresentative);
      // contactsMutation.mutate(addIndivisualToContactList.data);
    }

    onFresh();
  }, SAVE_DRAFT_DEBOUNCE_MS);

  const handleValuesChange = useMutableCallback(async (changedValues: any, values: Values) => {
    if (typeof onValuesChange === 'function') {
      onValuesChange(changedValues, values);
    }
    if (withAutoSaveDraft && shouldSaveDraft(changedValues, values)) {
      onStale();
      await debouncedSaveChanges(values);
    }
    optionalFieldsController.debouncedUpdate(values);
    handleCommonErrors.clearError();
  });

  // Submit
  const submitMutation = useSubmitStep();

  const onFinish = async (values: Values) => {
    let isSynchronized;

    const isApplicationChanged = getChangedPaths(
      applicationPayloadStepData,
      draftStepData as Array<Record<string, any>>,
      uiKey,
    );

    if (!isNewClient && isApplicationChanged?.length) {
      const confirmed = await new Promise<boolean>((res) => {
        Modal.confirm({
          title: t('Data Modification Confirmation'),
          content: t('Data has been modified. Do you want to save'),
          icon: null,
          centered: true,
          okText: t('Save and Continue'),
          okButtonProps: {
            id: 'companyType_confirmButton',
          },
          cancelText: t('Cancel and Return'),
          cancelButtonProps: { id: 'companyType_cancelButton' },
          onOk: () => {
            isSynchronized = false;
            res(true);
          },
          onCancel: () => res(false),
          width: 450,
        });
      });

      if (!confirmed) {
        return Promise.resolve();
      }
    }

    const result = parseBeforeSubmit ? await parseBeforeSubmit(values) : values;
    if (stepKey === 'servicesDetails') {
      const response = onBeforeSubmit();
      if (!response) {
        return null;
      }
    }

    if (onBeforeFinish) {
      await onBeforeFinish(result);
    }

    if (withDefaultOnFinish) {
      const fullPayload = { ...applicationQuery.data?.payload, ...values };
      const errors = validatorController.validateFn?.(fullPayload, getRoles());
      if (!isEmpty(errors)) {
        // eslint-disable-next-line
        const errorFields = Object.entries(errors!).map(([errName, errMessage]) => ({
          name: errName.split('.'),
          errors: [errMessage],
        }));
        onFinishFailed?.({ values: fullPayload, errorFields, outOfDate: false });
        handleCommonErrors.onError(errorFields);
        return Promise.reject(errors);
      }

      await submitMutation.mutateAsync({ stepData: result, params: { isSynchronized } });
    }
    dispatch(setAddingExistingUserValues({} as any));
    return Promise.resolve();
  };

  useEffect(() => {
    return () => {
      handleCommonErrors.clearError();
    };
  }, [handleCommonErrors]);

  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  if (isFormNotReady) return <Spin />;
  return (
    <FormItemContextProvider
      value={{
        disabledFields: 'xx',
        defaultValidator: withDefaultValidation ? validatorController.validator : undefined,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields ?? {},
        errorFields: stagePayload?.activeStep.errorInformation?.errorFields,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabled: isReadOnly(applicationQuery.data),
        disabledByDefault: disabled || isReadOnly(applicationQuery.data) || isSomeFieldsDisabled(applicationQuery.data),
        cardholderDetails: cardholderErrors
          ? {
              initialCardholderData: cardholderErrors?.data,
              status: cardholderErrors?.data?.status,
              errors: cardholderErrors?.data?.errors,
            }
          : null,
      }}
    >
      <FormWithMemo
        onValuesChange={handleValuesChange}
        initialValues={initialValues}
        name={actualFormName}
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        {...rest}
      />
    </FormItemContextProvider>
  );
}

const FormWithMemo = React.memo(Form);

const { Item, List, useForm, Provider, Visible, ShouldUpdate } = Form;

WizardForm.Item = Item;
WizardForm.List = List;
WizardForm.useForm = useForm;
WizardForm.useWatch = useWatch;
WizardForm.Provider = Provider;
WizardForm.Visible = Visible;
WizardForm.ShouldUpdate = ShouldUpdate;

export default WizardForm;
